export type DictationMode = 'flexible' | 'strict';

export type DictationTag = 'ok' | 'near' | 'wrong' | 'missing' | 'extra';

export type DictationWord = {
  expected: string;
  heard: string;
  tag: DictationTag;
};

export type DictationScore = {
  words: DictationWord[];
  scorePct: number;
  points: number;
  denominator: number;
  expectedCount: number;
  submittedCount: number;
};

type Token = {
  raw: string;
  value: string;
};

export function normalizeFlexibleWord(raw: string): string {
  return String(raw || '')
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[^a-z0-9']/g, '')
    .trim();
}

function tokenize(text: string, mode: DictationMode): Token[] {
  return String(text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((raw) => ({
      raw,
      value: mode === 'flexible' ? normalizeFlexibleWord(raw) : raw,
    }))
    .filter((token) => token.value.length > 0);
}

function charDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const previous = new Array(b.length + 1);
  const current = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j += 1) previous[j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    current[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const substitution = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + substitution
      );
    }
    for (let j = 0; j <= b.length; j += 1) previous[j] = current[j];
  }

  return previous[b.length];
}

function pairTag(expected: Token | null, heard: Token | null, mode: DictationMode): DictationTag {
  if (!expected) return 'extra';
  if (!heard) return 'missing';
  if (expected.value === heard.value) return 'ok';
  if (mode === 'strict') return 'wrong';

  const distance = charDistance(expected.value, heard.value);
  const longest = Math.max(expected.value.length, heard.value.length);
  if (distance <= 1 || (longest >= 5 && distance <= 2)) return 'near';
  return 'wrong';
}

export function scoreDictation(
  expectedText: string,
  submittedText: string,
  mode: DictationMode
): DictationScore {
  const expected = tokenize(expectedText, mode);
  const submitted = tokenize(submittedText, mode);
  const rows = expected.length;
  const columns = submitted.length;
  const dp: number[][] = [];

  for (let i = 0; i <= rows; i += 1) {
    dp[i] = [];
    for (let j = 0; j <= columns; j += 1) {
      if (i === 0) dp[i][j] = j;
      else if (j === 0) dp[i][j] = i;
      else {
        const substitution = expected[i - 1].value === submitted[j - 1].value ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + substitution
        );
      }
    }
  }

  const pairs: Array<{ expected: Token | null; heard: Token | null }> = [];
  let i = rows;
  let j = columns;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0) {
      const substitution = expected[i - 1].value === submitted[j - 1].value ? 0 : 1;
      if (dp[i][j] === dp[i - 1][j - 1] + substitution) {
        pairs.push({ expected: expected[i - 1], heard: submitted[j - 1] });
        i -= 1;
        j -= 1;
        continue;
      }
    }
    if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
      pairs.push({ expected: expected[i - 1], heard: null });
      i -= 1;
      continue;
    }
    pairs.push({ expected: null, heard: submitted[j - 1] });
    j -= 1;
  }
  pairs.reverse();

  const words = pairs.map((pair) => ({
    expected: pair.expected ? pair.expected.raw : '',
    heard: pair.heard ? pair.heard.raw : '',
    tag: pairTag(pair.expected, pair.heard, mode),
  }));

  let points = 0;
  words.forEach((word) => {
    if (word.tag === 'ok') points += 1;
    else if (word.tag === 'near') points += 0.5;
  });
  const denominator = Math.max(expected.length, submitted.length);

  return {
    words,
    scorePct: denominator ? Math.round((points / denominator) * 100) : 0,
    points,
    denominator,
    expectedCount: expected.length,
    submittedCount: submitted.length,
  };
}
